import axios from 'axios';

export const API_URL = process.env.REACT_APP_API_URL || 'https://nuroollah.ntssoftweb.com/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 8000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(config => {
  // If the browser itself reports no connection, fail instantly instead of
  // waiting on a network attempt (DNS/connect/timeout can otherwise take
  // several seconds before the offline fallback logic gets a chance to run).
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    const offlineError = new Error('Offline');
    offlineError.code = 'ERR_NETWORK';
    offlineError.config = config;
    return Promise.reject(offlineError);
  }
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// The server ROTATES refresh tokens: a refresh token works exactly once. After
// being offline longer than the access token's lifetime (1h), the first thing
// the app does on reconnect is fire several requests at once (profile, the
// page's own data, the sync pull ...) — they all get 401 together, each used to
// start its own refresh with the same token, the first one won and the others
// were rejected, which logged the person out and wiped their tokens. All
// concurrent 401s now share ONE refresh.
let refreshing = null;
function refreshTokens() {
  if (!refreshing) {
    refreshing = (async () => {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const res = await axios.post(`${API_URL}/auth/refresh`, { refreshToken }, { timeout: 15000 });
      const next = res.data.data;
      localStorage.setItem('accessToken', next.accessToken);
      localStorage.setItem('refreshToken', next.refreshToken);
      return next.accessToken;
    })().finally(() => { refreshing = null; });
  }
  return refreshing;
}

api.interceptors.response.use(
  response => response,
  async error => {
    const original = error.config || {};
    if (!error.response) return Promise.reject(error);
    if (
      error.response.status === 401 &&
      !original._retry &&
      !original.url?.includes('/settings') &&
      !original.url?.includes('/auth/refresh')
    ) {
      original._retry = true;
      try {
        const accessToken = await refreshTokens();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${accessToken}`;
        return api(original);
      } catch (refreshError) {
        // Only a real rejection of the refresh token ends the session. If the
        // refresh itself couldn't reach the server (connection dropped, server
        // restarting) the tokens are still good — wiping them logged people out
        // just because the network blinked, and queued offline work then had no
        // session to sync with.
        const rejected = refreshError?.response?.status || refreshError?.message === 'No refresh token';
        if (!rejected) return Promise.reject(error);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        if (window.location.pathname !== '/login') window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export { api };
export default api;
